import { emptyPlayerStat, type PlayerStatSource, type ProjectState } from "@livcg/schema";
import { RefreshCw } from "lucide-react";
import { sendRaw } from "../../../shared/sendRaw.js";
import { Badge, Button, cn, SegmentedControl, useToast } from "../../../ui/index.js";
import { useMatchDetail } from "../../useMatchDetail.js";
import { PlayerPicker } from "./PlayerPicker.js";
import {
  describeCompareError,
  formatClock,
  pickerRows,
  statusLabel,
  statusTone,
} from "./playerCompareView.js";

const MODE = "player_stat";

type Props = {
  slug: string;
  state: ProjectState;
  mappedIds: Map<string, number | null>;
  mappedLoading: boolean;
};

/** Single-player titre: the selection goes to the extension (`playerStat.set`), the result
 *  comes back from the replicant; "Efirga" puts the computed payload out as a lower-third. */
export function PlayerStatSection({ slug, state, mappedIds, mappedLoading }: Props) {
  const ps = state.playerStat ?? emptyPlayerStat();
  const { toast } = useToast();
  const { detail } = useMatchDetail(state);
  const onAir = state.overlay.mode === MODE;

  const send = (name: string, payload: Record<string, unknown>) =>
    void sendRaw(name, { slug, ...payload }).catch((e: Error) =>
      toast({ tone: "error", title: "Yuborib bo'lmadi", description: e.message }),
    );
  const set = (patch: { playerId?: string | null; source?: PlayerStatSource }) =>
    send("playerStat.set", patch);

  const ready = ps.status === "ready" && !!ps.payload && ps.payload.items.length > 0;

  const show = () => {
    if (!ready || !ps.payload) return;
    send("overlay.show", { mode: MODE, data: ps.payload });
  };
  const hide = () => send("overlay.off", { mode: MODE });

  const p = ps.payload;
  return (
    <div className="flex flex-col gap-5">
      <p className="text-[12px] text-ink-muted">
        {"Bitta o'yinchini tanlang — pozitsiyasiga qarab 2–3 ko'rsatkich dostats'dan " +
          "hisoblanadi va pastki titr bo'lib chiqadi. Mavsum = joriy mavsum jami, " +
          "Shu o'yin = joriy o'yinning jonli ma'lumoti."}
      </p>

      <div className="flex items-center gap-2">
        <span className="text-[12px] text-ink-muted">Davr</span>
        <SegmentedControl
          size="sm"
          value={ps.source}
          onChange={(v) => set({ source: v })}
          options={[
            { value: "season", label: "Mavsum" },
            { value: "match", label: "Shu o'yin" },
          ]}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <PlayerPicker
          title={`Uy · ${state.teams.home?.shortName ?? ""}`}
          rows={pickerRows(detail, "home")}
          selectedId={ps.playerId}
          mappedIds={mappedIds}
          mappedLoading={mappedLoading}
          onSelect={(id) => set({ playerId: id })}
        />
        <PlayerPicker
          title={`Mehmon · ${state.teams.away?.shortName ?? ""}`}
          rows={pickerRows(detail, "away")}
          selectedId={ps.playerId}
          mappedIds={mappedIds}
          mappedLoading={mappedLoading}
          onSelect={(id) => set({ playerId: id })}
        />
      </div>

      <div className="flex flex-col gap-2 rounded-lg border border-border bg-surface p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Badge tone={statusTone(ps.status)} dot pulse={ps.status === "loading"}>
              {statusLabel(ps.status)}
            </Badge>
            {onAir && <Badge tone="live">Efirda</Badge>}
            {p && <span className="text-[11px] text-ink-faint">{formatClock(p.fetchedAt)}</span>}
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="secondary"
              size="xs"
              onClick={() => send("playerStat.refresh", {})}
              disabled={ps.status === "loading" || !ps.playerId}
              leadingIcon={<RefreshCw className="h-3 w-3" />}
            >
              Yangilash
            </Button>
            {onAir && (
              <Button variant="secondary" size="xs" onClick={hide}>
                Yashirish
              </Button>
            )}
            <Button variant="live" size="xs" onClick={show} disabled={!ready}>
              {onAir ? "Efirda yangilash" : "Efirga"}
            </Button>
          </div>
        </div>
        {ps.status === "error" && (
          <p className="text-[12px] text-danger-strong">{describeCompareError(ps.error)}</p>
        )}
        {p ? (
          <div className={cn("flex flex-col gap-1.5", ps.status === "loading" && "opacity-50")}>
            <div className="text-[14px] text-ink">
              {p.firstName} <span className="font-semibold">{p.lastName}</span>
              <span className="ml-2 text-[11px] uppercase tracking-[0.1em] text-ink-muted">
                {p.pill}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {p.items.map((it) => (
                <span
                  key={it.icon}
                  className="rounded-md border border-divider px-2 py-1 text-[13px] font-semibold"
                >
                  {it.text}
                </span>
              ))}
            </div>
          </div>
        ) : (
          ps.status !== "error" && (
            <p className="text-[12px] text-ink-faint">
              O'yinchini tanlang — natija shu yerda ko'rinadi.
            </p>
          )
        )}
      </div>
    </div>
  );
}
